import React from "react";

import CheckCircle from "@uui/CheckCircle";

import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import AddReadInput from "@app/analytics/components/AddReadInput";
import useAddRead from "@app/analytics/hooks/meetings/useAddRead";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import InfoPanel from "@rui/InfoPanel";
import Link from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import { Container, ContentWrapper } from "./styles";

const AddRead = () => {
  usePageTitle("Add Read");

  const { addRead, hasInputError, meetingUrl, onInputChange, validate } =
    useAddRead({ uiLocation: "add_read_to_live_meeting_page" });

  const handleAddRead = () => {
    const isValid = validate(meetingUrl);
    if (isValid) {
      addRead.mutate({ meetingUrl });
    }
  };

  const hasError = hasInputError || addRead.isError;

  return (
    <Container variant="apart">
      <ContentWrapper spacing={2} variant="center">
        <Column textAlign="center" spacing={0.5}>
          <Typography variant="h1">Add Read to a live meeting</Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Copy and paste a meeting URL below to add your Read Assistant.
          </Typography>
        </Column>
        <Column minHeight="48px" variant="center" width="100%">
          {addRead.isPending ? (
            <Row variant="center" spacing={2}>
              <LoadSpinner size={16} />
              <Typography variant="bodyPrimaryReg">
                Adding Read to your meeting...
              </Typography>
            </Row>
          ) : addRead.isSuccess ? (
            <Row variant="center" spacing={1}>
              <CheckCircle
                sx={{ fontSize: "16px", color: "shades.meadow.600" }}
              />
              <Typography variant="bodyPrimaryReg">
                Success! Read has been added to your meeting.
              </Typography>
            </Row>
          ) : (
            <Column spacing={1} width="100%" variant="center">
              <AddReadInput
                error={hasError}
                fullWidth
                onChange={onInputChange}
                onSubmit={handleAddRead}
                value={meetingUrl}
                sx={{ minWidth: "100%" }}
              />
              <Collapse in={hasError}>
                {addRead.isError ? (
                  <InfoPanel severity="error" sx={{ textAlign: "initial" }}>
                    <Typography variant="labelReg" lineHeight={1}>
                      <Bold>Error:</Bold> Read was unable to join the meeting
                      URL provided. Please check the URL and try again, or view
                      more ways to{" "}
                      <Link href={LINKS.SUPPORT.MANUALLY_ADD_READ}>
                        troubleshoot
                      </Link>
                      .
                    </Typography>
                  </InfoPanel>
                ) : hasInputError ? (
                  <InfoPanel severity="error">
                    <Typography variant="labelReg" lineHeight={1}>
                      <Bold>Error:</Bold> Please input a valid https URL
                    </Typography>
                  </InfoPanel>
                ) : null}
              </Collapse>
            </Column>
          )}
        </Column>
        <Link
          href={LINKS.SUPPORT.FIND_MEETING_URL}
          underline
          variant="labelMed"
        >
          Where do I find my meeting URL?
        </Link>
      </ContentWrapper>
      <Row spacing={1} color="shades.neutral.600">
        <Typography variant="bodySecondaryReg">Go to:</Typography>
        <Link variant="bodySecondaryReg" underline to={ROUTES.CALENDAR}>
          Calendar
        </Link>
        <Typography variant="bodySecondaryReg">|</Typography>
        <Link variant="bodySecondaryReg" underline to={ROUTES.MEETING_REPORTS}>
          Reports
        </Link>
      </Row>
    </Container>
  );
};

export default AddRead;
